import React from 'react';
import {
  Create,
  Edit,
  EditButton,
  Filter,
  maxLength,
  minLength,
  NumberField,
  ReferenceField,
  ReferenceInput,
  required,
  SearchInput,
  TextField,
  TextInput,
} from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import { ZippityDatagrid, ZippitySimpleForm } from '../components';
import ImageLinkField from './ImageLinkField';
import ImageUploadSelector from './ImageUploadSelector';
import LatLngField from './LatLngField';
import LatLngInput from './LatLngInput';
import BulkDeleteButton from '../components/BulkDeleteButton';
import CustomTitle from '../components/CustomTitle';
import ReferenceChild from '../components/ReferenceChild';
import { getZ3PConfigValue, perPage } from '../helper';
import ListWithCSVImport from '../components/ListWithCSVImport';

const ParkingFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    <ReferenceInput
      source="client_location_id"
      reference="client-location"
      sort={{ field: 'long_name', order: 'ASC' }}
      perPage={perPage}
      alwaysOn
    >
      <ReferenceChild
        {...props}
        optionText="long_name"
        source="client_location_id"
      />
    </ReferenceInput>
  </Filter>
);

const colored = (WrappedComponent) => {
  const Colored = (props) => {
    const { record, source } = props;
    if (record[source] > 500) {
      return (
        <span style={{ color: 'red' }}>
          <WrappedComponent {...props} />
        </span>
      );
    }
    return <WrappedComponent {...props} />;
  };

  Colored.displayName = `Colored(${WrappedComponent.displayName})`;

  return Colored;
};

export const ColoredNumberField = colored(NumberField);
ColoredNumberField.defaultProps = NumberField.defaultProps;

const listStyles = {
  nb_commands: { color: 'purple' },
};

const isUsingHelpText = getZ3PConfigValue('is_using_help_text');

export const ParkingList = withStyles(listStyles)(({ classes, ...props }) => (
  <div>
    <ListWithCSVImport
      {...props}
      filters={<ParkingFilter />}
      bulkActionButtons={<BulkDeleteButton />}
      sort={{ field: 'client_location_id', order: 'ASC' }}
      perPage={50}
    >
      <ZippityDatagrid>
        <TextField source="created_at" />
        <TextField source="modified_at" />
        <TextField source="parking_id" />
        <ReferenceField
          source="client_location_id"
          reference="client-location"
          sortable={false}
        >
          <TextField source="short_name" />
        </ReferenceField>
        <TextField source="name" />
        <LatLngField source="lat-lng" prefix="" />
        <TextField source="blurb" />
        <ImageLinkField source="image_url" />
        <EditButton />
      </ZippityDatagrid>
    </ListWithCSVImport>

    {isUsingHelpText && (
      <div>
        The <strong>Parking Zone</strong> is the description of where our
        customers should park their cars.
        <ul>
          <li>
            Every Client Location should have at least one Parking Zone before
            we go live.
          </li>
          <li>
            If there&apos;s no Parking Zone for a location, we use the name
            &quot;Main Parking Area&quot;.
          </li>
          <li>
            If there&apos;s no Parking Zone Image/Blurb present, we skip them.
          </li>
          <li>
            The <strong>Name, Blurb</strong> and <strong>Image</strong> are
            displayed to the customer -- both in the booking app, and in
            transactional emails.
          </li>
          <li>
            The <strong>Latitude/Longitude</strong> are used to give the
            customer a Google Maps link. They should have at least 6 digits of
            precision.
          </li>
        </ul>
      </div>
    )}
  </div>
));

const editStyles = {};

const validateName = [required(), minLength(2), maxLength(255)];
const validateBlurb = [maxLength(1023)];

export const ParkingEdit = withStyles(editStyles)(({ classes, ...props }) => (
  <Edit
    undoable={false}
    title={<CustomTitle title="resources.parking.title" />}
    {...props}
  >
    <ZippitySimpleForm>
      <ReferenceInput
        source="client_location_id"
        reference="client-location"
        sort={{ field: 'long_name', order: 'ASC' }}
        perPage={perPage}
        validate={[required()]}
      >
        <ReferenceChild
          {...props}
          optionText="long_name"
          source="client_location_id"
        />
      </ReferenceInput>
      <TextInput validate={validateName} source="name" />
      <LatLngInput prefix="" displayAtTop />
      <TextInput multiline validate={validateBlurb} source="blurb" />
      <ImageUploadSelector source="image_url" folder="parking" />
    </ZippitySimpleForm>
  </Edit>
));

export const ParkingCreate = withStyles(editStyles)(({ classes, ...props }) => (
  <Create {...props}>
    <ZippitySimpleForm>
      <ReferenceInput
        source="client_location_id"
        reference="client-location"
        sort={{ field: 'long_name', order: 'ASC' }}
        perPage={perPage}
        validate={[required()]}
      >
        <ReferenceChild
          {...props}
          optionText="long_name"
          source="client_location_id"
        />
      </ReferenceInput>
      <TextInput validate={validateName} source="name" />
      <LatLngInput prefix="" displayAtTop />
      <TextInput multiline validate={validateBlurb} source="blurb" />
      <ImageUploadSelector source="image_url" folder="parking" />
    </ZippitySimpleForm>
  </Create>
));
